/**
 * Emerald Books — Employee Self-Service (ESS) portal. Mobile-first hub.
 * Shows: my open tasks, my tickets, my leave balance, quick actions.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, ListChecks, Ticket, CalendarClock, Palmtree, FileText,
  MessageSquarePlus, ClipboardList, ArrowRight, Coins, Download, BookOpen,
} from 'lucide-react';
import { booksClient, errMsg, getBooksUser, unwrapList } from './api';

const Tile = ({ icon: Icon, label, value, hint, to, testid, accent = '#0f5c3d' }) => (
  <Link to={to} data-testid={testid}
    className="bg-white rounded-lg border p-4 hover:shadow-md transition flex items-center gap-3 group">
    <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: accent + '18', color: accent }}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-xs text-slate-500 truncate">{label}</div>
      <div className="text-xl font-semibold text-slate-800">{value}</div>
      {hint && <div className="text-[10px] text-slate-400 truncate">{hint}</div>}
    </div>
    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#0f5c3d]" />
  </Link>
);

export const ESSHomePage = () => {
  const [loading, setLoading] = useState(true);
  const [crm, setCrm] = useState(null);
  const [myLeaves, setMyLeaves] = useState([]);
  const user = getBooksUser();

  useEffect(() => {
    (async () => {
      try {
        const [c, l] = await Promise.all([
          booksClient.get('/crm/summary'),
          booksClient.get('/leave/requests', { params: { mine: true } }),
        ]);
        setCrm(c.data);
        setMyLeaves(unwrapList(l));
      } catch (e) { toast.error(errMsg(e)); }
      setLoading(false);
    })();
  }, []);

  const pending = myLeaves.filter((r) => r.status === 'pending').length;
  const approved = myLeaves.filter((r) => r.status === 'approved').length;

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Welcome</div>
        <h1 className="text-2xl font-semibold text-[#0b1a2e]">{user?.name || 'Team member'}</h1>
        <p className="text-sm text-slate-500">Your workspace · everything you need in one place</p>
      </div>

      {loading ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Tile icon={ListChecks} label="My open tasks" value={crm?.my_open_tasks ?? 0}
              hint="Assigned to me" to="/books/app/crm/tasks" testid="ess-tile-tasks" />
            <Tile icon={Ticket} label="My open tickets" value={crm?.my_open_tickets ?? 0}
              hint="Support requests" to="/books/app/crm/tickets" testid="ess-tile-tickets" accent="#6366f1" />
            <Tile icon={CalendarClock} label="Pending leave" value={pending}
              hint={`${approved} approved this year`} to="/books/app/leave/requests" testid="ess-tile-leave" accent="#f59e0b" />
          </div>

          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2">Quick actions</div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <QuickAction icon={Palmtree} label="Request leave" to="/books/app/leave/requests" testid="ess-quick-leave" />
              <QuickAction icon={MessageSquarePlus} label="Raise ticket" to="/books/app/crm/tickets" testid="ess-quick-ticket" />
              <QuickAction icon={Coins} label="File expense" to="/books/app/expenses" testid="ess-quick-expense" />
              <QuickAction icon={FileText} label="Policies" to="/books/app/hr/policies" testid="ess-quick-policies" />
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2">Recent leave activity</div>
            <div className="space-y-2">
              {myLeaves.slice(0, 5).length === 0 && (
                <div className="text-sm text-slate-400 bg-white border rounded-lg p-4 text-center">No leave records yet</div>
              )}
              {myLeaves.slice(0, 5).map((r) => (
                <div key={r.request_id} className="bg-white rounded-lg border p-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{r.leave_type_name} · {r.days}d</div>
                    <div className="text-xs text-slate-500">{r.start_date} → {r.end_date}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                    r.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                    r.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                    r.status === 'rejected' ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-500'
                  }`}>{r.status}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2">Documentation</div>
            <div className="grid gap-2 sm:grid-cols-3">
              <a href="/docs/Emerald_Screenshot_Manual.pdf" target="_blank" rel="noreferrer"
                data-testid="ess-screenshot-manual-pdf"
                className="bg-white border rounded-lg p-3 flex items-center gap-3 hover:shadow-md transition group">
                <div className="w-10 h-10 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">Screenshot Manual</div>
                  <div className="text-[11px] text-slate-500 truncate">Screen-by-screen · 70 pages</div>
                </div>
                <Download className="w-4 h-4 text-slate-300 group-hover:text-[#0f5c3d]" />
              </a>
              <a href="/docs/Emerald_Testing_Manual.pdf" target="_blank" rel="noreferrer"
                data-testid="ess-testing-manual-pdf"
                className="bg-white border rounded-lg p-3 flex items-center gap-3 hover:shadow-md transition group">
                <div className="w-10 h-10 rounded bg-rose-50 text-rose-600 flex items-center justify-center">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">Testing Manual</div>
                  <div className="text-[11px] text-slate-500 truncate">Test cases · priorities · testids</div>
                </div>
                <Download className="w-4 h-4 text-slate-300 group-hover:text-[#0f5c3d]" />
              </a>
              <a href="/docs/Emerald_User_Manual.pdf" target="_blank" rel="noreferrer"
                data-testid="ess-user-manual-pdf"
                className="bg-white border rounded-lg p-3 flex items-center gap-3 hover:shadow-md transition group">
                <div className="w-10 h-10 rounded bg-emerald-50 text-emerald-700 flex items-center justify-center">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">User Manual</div>
                  <div className="text-[11px] text-slate-500 truncate">How each feature works</div>
                </div>
                <Download className="w-4 h-4 text-slate-300 group-hover:text-[#0f5c3d]" />
              </a>
            </div>
            <div className="mt-1 text-[11px] text-slate-400">
              Full library at <a className="underline" href="/docs">/docs</a>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const QuickAction = ({ icon: Icon, label, to, testid }) => (
  <Link to={to} data-testid={testid}
    className="bg-gradient-to-br from-[#0f5c3d] to-[#0b1a2e] text-white rounded-lg p-4 flex flex-col items-start gap-2 hover:shadow-lg transition group">
    <Icon className="w-6 h-6 opacity-80 group-hover:scale-110 transition" />
    <div className="text-sm font-medium">{label}</div>
  </Link>
);

export default ESSHomePage;
